// getMessageAesMediaSource.js
'use strict';
import createCachedSelector from '../resource/createCachedSelector.js';

import getMessageData from '../selector/getMessageData.js';
import getAssetData from '../selector/getAssetData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { PREVIEW_PRESET, PUBLIC_URL_PREFIX } from '../RemoteConfigKeys.js';
import {
  getAssetTrailer,
  MediaAssetFormat,
  MediaPreset,
} from '../resource/getMediaAsset.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';

const buildFullUrl = ({ relativePath }) => {
  if (!relativePath) return undefined;
  return getResourceUrl({ endpoint: relativePath, resourceType: PUBLIC }).href;
};

/**
 * Select aes media source by message id and asset id
 * @kind selector
 * @param {Immutable.Map} state - root state.
 * @param {string} messageId - message id.
 * @param {string} assetId - asset id.
 * @return {object} The selected aes media source { hls, mp4 }.
 */
const getMessageAesMediaSource = createCachedSelector(
  (state, messageId) => messageId,
  (state, messageId, assetId) => assetId,
  (state, messageId) => getMessageData(state, messageId, 'canViewMedia'),
  (state, messageId, assetId) => getAssetData(state, [assetId], 'url'),
  (state, messageId, assetId) => getAssetData(state, [assetId], 'blurredUrl'),
  (state, messageId) => getMessageData(state, messageId, 'trailerUrl'),
  state => getRemoteConfigData(state, PREVIEW_PRESET),
  state => getRemoteConfigData(state, PUBLIC_URL_PREFIX),
  (
    messageId,
    assetId,
    canViewMedia,
    assetUrl,
    blurredUrl,
    trailerUrl,
    previewPreset,
    // Use `PUBLIC_URL_PREFIX` to bust cached selector on SSR when different users have different public URL prefixes.
    // eslint-disable-next-line no-unused-vars
    publicUrlPrefix
  ) => {
    const fallbackTrailerUrl = getAssetTrailer({
      id: messageId,
      assetId,
      format: MediaAssetFormat.MP4,
      preset:
        previewPreset === 'clear' ? MediaPreset.SD : MediaPreset.SD_PREVIEW,
      duration: 10,
    }).href;

    return {
      hls: buildFullUrl({
        relativePath: canViewMedia ? assetUrl : blurredUrl,
      }),
      mp4: buildFullUrl({ relativePath: trailerUrl }) || fallbackTrailerUrl,
    };
  }
)(
  (state, messageId, assetId) =>
    `getMessageAesMediaSource:${messageId}:${assetId}`
);

export default getMessageAesMediaSource;
